
import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  message,
  Row,
  Col,
  Statistic,
  Input,
  Select,
  Typography,
  Tooltip,
  Modal,
  Upload,
  Form,
  DatePicker,
  Switch,
  Descriptions,
  Badge,
  Tabs,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  DownloadOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileOutlined,
  UploadOutlined,
  SearchOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import apiService from "services/api/apiService";
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const DocumentManagement: React.FC = () => {
  const { user, isEmployee } = useAuth();
  const [documents, setDocuments] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [searchText, setSearchText] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [docTab, setDocTab] = React.useState('documents');
  const [expiryAlerts, setExpiryAlerts] = React.useState<any>({ expiring_soon: [], already_expired: [] });
  const [docVersions, setDocVersions] = React.useState<any[]>([]);
  const [signatures, setSignatures] = React.useState<any[]>([]);
  const [selectedDocForVersion, setSelectedDocForVersion] = React.useState<any>(null);
  const [signatureData, setSignatureData] = React.useState('');
  const [signDoc, setSignDoc] = React.useState<any>(null);
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = React.useState(false);

  const fetchExpiryAlerts = async () => {
    const r = await apiService.getDocumentExpiryAlerts(30);
    if (r.success) setExpiryAlerts(r);
  };

  React.useEffect(() => { if (docTab === 'expiry') fetchExpiryAlerts(); }, [docTab]);

  const fetchVersions = async (doc: any) => {
    setSelectedDocForVersion(doc);
    const r = await apiService.getDocumentVersions(doc.id);
    if (r.success) setDocVersions(r.data || []);
    setDocTab('versions');
  };

  const startDraw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.beginPath(); ctx.moveTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY); }
  };
  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx) { ctx.lineWidth = 2; ctx.strokeStyle = '#1890ff'; ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY); ctx.stroke(); }
  };
  const stopDraw = () => {
    setIsDrawing(false);
    setSignatureData(canvasRef.current?.toDataURL() || '');
  };
  const clearSignature = () => {
    const ctx = canvasRef.current?.getContext('2d');
    if (ctx && canvasRef.current) ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    setSignatureData('');
  };
  const saveSignature = async () => {
    if (!signatureData) { message.warning('Please sign the document first'); return; }
    if (!signDoc) { message.warning('Please select a document to sign'); return; }
    try {
      // Decode JWT to get employee ID
      let empId = null;
      try {
        const token = localStorage.getItem('hr_access_token') || '';
        const payload = JSON.parse(atob(token.split('.')[1] || token));
        empId = payload.id || payload.employee_id || payload.sub || null;
      } catch(e) { empId = null; }
      const response = await fetch(
        `https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=save_signature&tenant_id=${apiService.getTenantId()}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            document_id: signDoc.id,
            employee_id: empId,
            signature_data: signatureData,
            tenant_id: apiService.getTenantId()
          })
        }
      );
      const data = await response.json();
      if (data.success) {
        message.success(`Document signed successfully at ${data.signed_at}`);
        clearSignature();
        setSignDoc(null);
        // Refresh signatures list
        fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=get_signatures&tenant_id=${apiService.getTenantId()}&document_id=all`)
          .then(r => r.json()).then(d => { if (d.success) setSignatures(d.data || []); });
      } else {
        message.error(data.error || 'Failed to save signature');
      }
    } catch (e) {
      message.error('Failed to save signature');
    }
  };

  const [form] = Form.useForm();

  useEffect(() => {
    fetchDocuments();
    fetchEmployees();
  }, []);

  const fetchDocuments = async () => {
  try {
    setLoading(true);
    const response = await apiService.getDocuments();
    
    if (response.success) {
      // The API returns {success: true, documents: [...]}
      let docData = response.documents || response.data || [];
      
      // Ensure it's always an array
      if (!Array.isArray(docData)) {
        if (docData.documents) {
          docData = docData.documents;
        } else if (docData.data) {
          docData = docData.data;
        } else {
          docData = [];
        }
      }
      
      let finalDocs = Array.isArray(docData) ? docData : [];
      if (isEmployee() && user?.employee_id) {
        finalDocs = finalDocs.filter((d: any) => String(d.employee_id) === String(user.employee_id));
      }
      setDocuments(finalDocs);
    } else {
      setDocuments([]);
    }
  } catch (error) {
    console.error('Documents fetch error:', error);
    message.error('Failed to fetch documents');
    setDocuments([]);
  } finally {
    setLoading(false);
  }
};
  const fetchEmployees = async () => {
    try {
      const response = await apiService.getEmployees();
      if (response.success) {
        let empData = response.data || [];
        if (!Array.isArray(empData)) {
          empData = empData.employees || empData.data || [];
        }
        setEmployees(Array.isArray(empData) ? empData : []);
      }
    } catch (error) {
      console.error('Employees fetch error:', error);
    }
  };

  const handleUpload = async (values: any) => {
  try {
    setLoading(true);
    console.log('📤 Upload form values:', values);
    
    const formData = new FormData();
    formData.append('employee_id', values.employee_id);
    formData.append('document_type', values.document_type);
    formData.append('title', values.title);
    formData.append('description', values.description || '');
    formData.append('is_confidential', values.is_confidential ? '1' : '0');
    formData.append('access_level', values.access_level);
    formData.append('tenant_id', apiService.getTenantId());
    const currentUser = JSON.parse(atob(localStorage.getItem('hr_access_token') || ''));
    formData.append('uploaded_by', String(currentUser?.id || ''));
    if (values.expiry_date && values.expiry_date.isValid()) {
  formData.append('expiry_date', values.expiry_date.format('YYYY-MM-DD'));
}
    // Handle file upload properly
    if (values.file && values.file.length > 0) {
      const file = values.file[0].originFileObj || values.file[0];
      formData.append('document_file', file);
      console.log('📎 File to upload:', file.name);
    } else {
      message.error('Please select a file to upload');
      return;
    }

    console.log('📤 Uploading document...');
    const response = await apiService.uploadDocument(formData);
    console.log('📥 Upload response:', response);
    
    if (response.success) {
      message.success('Document uploaded successfully');
      setUploadModalVisible(false);
      form.resetFields();
      fetchDocuments();
    } else {
      message.error(response.error || 'Upload failed');
    }
  } catch (error) {
    console.error('💥 Upload error:', error);
    message.error('Upload failed');
  } finally {
    setLoading(false);
  }
};
  const handleDelete = async (documentId: string) => {
    try {
      setLoading(true);
      const response = await apiService.deleteDocument(documentId);
      
      if (response.success) {
        message.success('Document deleted successfully');
        fetchDocuments();
      } else {
        message.error(response.error || 'Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      message.error('Delete failed');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: any) => {
  try {
    const response = await fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/download.php?id=${doc.id}&tenant_id=${apiService.getTenantId()}`);
    
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = doc.title || 'document';
      window.document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(url);
      window.document.body.removeChild(link);
      message.success('Download started');
    } else {
      message.error('Download failed');
    }
  } catch (error) {
    console.error('Download error:', error);
    message.error('Download failed');
  }
};

  const handleView = (document: any) => {
    setSelectedDocument(document);
    setViewModalVisible(true);
  };

  const getDocumentTypeColor = (type: string) => {
    const colors = {
      'resume': 'blue',
      'contract': 'green',
      'certificate': 'purple',
      'policy': 'orange',
      'report': 'cyan',
      'other': 'default'
    };
    return colors[type as keyof typeof colors] || 'default';
  };

  const getStatusColor = (status: string) => {
    const colors = {
      'active': 'success',
      'archived': 'default',
      'expired': 'error'
    };
    return colors[status as keyof typeof colors] || 'default';
  };

  const safeDocuments = Array.isArray(documents) ? documents : [];
  
  const filteredDocuments = safeDocuments.filter(doc => {
    const matchesSearch = doc.title?.toLowerCase().includes(searchText.toLowerCase()) ||
                         doc.description?.toLowerCase().includes(searchText.toLowerCase()) ||
                         doc.first_name?.toLowerCase().includes(searchText.toLowerCase()) ||
                         doc.last_name?.toLowerCase().includes(searchText.toLowerCase());
    const matchesType = typeFilter === 'all' || doc.document_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || doc.status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  const documentColumns = [
    {
      title: 'Document Info',
      key: 'document',
      render: (record: any) => (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>
            <FileOutlined style={{ marginRight: 8 }} />
            {record.title}
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ID: {record.document_id}
          </div>
          <Tag color={getDocumentTypeColor(record.document_type)} style={{ marginTop: 4 }}>
            {record.document_type?.toUpperCase()}
          </Tag>
        </div>
      ),
    },
    {
      title: 'Employee',
      key: 'employee',
      render: (record: any) => (
        <div>
          <div>{record.first_name} {record.last_name}</div>
          <div style={{ fontSize: '12px', color: '#666' }}>ID: {record.employee_id}</div>
        </div>
      ),
    },
    {
      title: 'File Info',
      key: 'file',
      render: (record: any) => (
        <div>
          <div>Size: {(record.file_size / 1024).toFixed(1)} KB</div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            Type: {record.mime_type?.split('/').pop()?.toUpperCase()}
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: any) => (
        <div>
          <Tag color={getStatusColor(record.status)}>
            {record.status?.toUpperCase()}
          </Tag>
          {record.is_confidential === 1 && (
            <Badge count="Confidential" style={{ backgroundColor: '#f50' }} />
          )}
          {record.expiry_date && (
            <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
              Expires: {dayjs(record.expiry_date).format('MMM DD, YYYY')}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: any) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="text"
              icon={<DownloadOutlined />}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id.toString())}
              loading={loading}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Tabs activeKey={docTab} onChange={setDocTab} items={[
        {
          key: 'documents',
          label: '📁 All Documents',
          children: (
            <div>
                    <div style={{ marginBottom: '24px' }}>
                      <Title level={2}>
                        <FileOutlined /> Document Management
                      </Title>
                    </div>
              
                    {/* Statistics */}
                    <Row gutter={16} style={{ marginBottom: '24px' }}>
                      <Col span={6}>
                        <Card>
                          <Statistic
                            title="Total Documents"
                            value={safeDocuments.length}
                            prefix={<FileOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card>
                          <Statistic
                            title="Active Documents"
                            value={safeDocuments.filter(d => d.status === 'active').length}
                            prefix={<Badge status="success" />}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card>
                          <Statistic
                            title="Confidential"
                            value={safeDocuments.filter(d => d.is_confidential === 1).length}
                            prefix={<Badge status="error" />}
                          />
                        </Card>
                      </Col>
                      <Col span={6}>
                        <Card>
                          <Statistic
                            title="Total Size"
                            value={`${(safeDocuments.reduce((sum, d) => sum + (d.file_size || 0), 0) / 1024 / 1024).toFixed(1)} MB`}
                            prefix={<UploadOutlined />}
                          />
                        </Card>
                      </Col>
                    </Row>
              
                    {/* Filters */}
                    <Card style={{ marginBottom: '24px' }}>
                      <Row gutter={16} align="middle">
                        <Col flex="auto">
                          <Input
                            placeholder="Search documents..."
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                          />
                        </Col>
                        <Col>
                          <Select
                            value={typeFilter}
                            onChange={setTypeFilter}
                            style={{ width: 140 }}
                          >
                            <Option value="all">All Types</Option>
                            <Option value="resume">Resume</Option>
                            <Option value="contract">Contract</Option>
                            <Option value="certificate">Certificate</Option>
                            <Option value="policy">Policy</Option>
                            <Option value="report">Report</Option>
                            <Option value="other">Other</Option>
                          </Select>
                        </Col>
                        <Col>
                          <Select
                            value={statusFilter}
                            onChange={setStatusFilter}
                            style={{ width: 120 }}
                          >
                            <Option value="all">All Status</Option>
                            <Option value="active">Active</Option>
                            <Option value="archived">Archived</Option>
                            <Option value="expired">Expired</Option>
                          </Select>
                        </Col>
                        <Col>
                          <Button
                            icon={<ReloadOutlined />}
                            onClick={fetchDocuments}
                            loading={loading}
                            style={{ marginRight: 8 }}
                          >
                            Refresh
                          </Button>
                        </Col>
                        <Col>
                          <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                              if (isEmployee() && user?.employee_id) {
                                form.setFieldsValue({ employee_id: employees.find((e: any) => String(e.employee_id) === String(user.employee_id))?.id });
                              }
                              setUploadModalVisible(true);
                            }}
                          >
                            Upload Document
                          </Button>
                        </Col>
                      </Row>
                    </Card>
              
                    {/* Documents Table */}
                    <Card title="Documents">
                      <Table
                        columns={documentColumns}
                        dataSource={filteredDocuments}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                          pageSize: 10,
                          showSizeChanger: true,
                          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} documents`,
                        }}
                        scroll={{ x: 1200 }}
                      />
                    </Card>
              
                    {/* Upload Modal */}
                    <Modal
                      title="Upload Document"
                      open={uploadModalVisible}
                      onCancel={() => {
                        setUploadModalVisible(false);
                        form.resetFields();
                      }}
                      onOk={() => form.submit()}
                      confirmLoading={loading}
                      width={600}
                    >
                      <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleUpload}
                        style={{ marginTop: 20 }}
                      >
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item
                              name="employee_id"
                              label="Employee"
                              rules={[{ required: true, message: 'Please select employee' }]}
                            >
                              <Select placeholder="Select employee" disabled={isEmployee()}>
                                {employees.map(emp => (
                                  <Option key={emp.id} value={emp.id}>
                                    {emp.first_name} {emp.last_name} - {emp.employee_id}
                                  </Option>
                                ))}
                              </Select>
                            </Form.Item>
                          </Col>
                          <Col span={12}>
              	  <Form.Item
                name="document_type"
                label="Document Type"
                rules={[{ required: true, message: 'Please select type' }]}
              >
                <Select placeholder="Select type">
                  <Option value="resume">Resume</Option>
                  <Option value="contract">Contract</Option>
                  <Option value="certificate">Certificate</Option>
                  <Option value="offer_letter">Offer Letter</Option>
                  <Option value="id_copy">ID Copy</Option>
                  <Option value="performance_review">Performance Review</Option>
                  <Option value="disciplinary">Disciplinary</Option>
                  <Option value="other">Other</Option>
                </Select>
              </Form.Item>
                          </Col>
                        </Row>
              
                        <Form.Item
                          name="title"
                          label="Document Title"
                          rules={[{ required: true, message: 'Please enter title' }]}
                        >
                          <Input placeholder="Enter document title" />
                        </Form.Item>
              
                        <Form.Item
                          name="description"
                          label="Description"
                        >
                          <TextArea rows={3} placeholder="Enter description (optional)" />
                        </Form.Item>
              
                        <Row gutter={16}>
                          <Col span={12}>
              	  <Form.Item
                name="access_level"
                label="Access Level"
                rules={[{ required: true, message: 'Please select access level' }]}
              >
                <Select placeholder="Select access level">
                  <Option value="public">Public</Option>
                  <Option value="hr_only">HR Only</Option>
                  <Option value="manager">Manager</Option>
                  <Option value="employee_only">Employee Only</Option>
                </Select>
              </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item
                              name="expiry_date"
                              label="Expiry Date (Optional)"
                            >
                              <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                          </Col>
                        </Row>
              
                        <Form.Item
                          name="is_confidential"
                          valuePropName="checked"
                        >
                          <Switch /> Confidential Document
                        </Form.Item>
              
                        <Form.Item
                name="file"
                label="File"
                rules={[{ required: true, message: 'Please select file' }]}
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  if (Array.isArray(e)) {
                    return e;
                  }
                  return e && e.fileList;
                }}
              >
                <Upload
                  beforeUpload={() => false}
                  maxCount={1}
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png"
                >
                  <Button icon={<UploadOutlined />}>Select File</Button>
                </Upload>
              </Form.Item>
                      </Form>
                    </Modal>
              
                    {/* View Modal */}
                    <Modal
                      title="Document Details"
                      open={viewModalVisible}
                      onCancel={() => setViewModalVisible(false)}
                      footer={[
                        <Button key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(selectedDocument)}>
                          Download
                        </Button>,
                        <Button key="close" onClick={() => setViewModalVisible(false)}>
                          Close
                        </Button>
                      ]}
                      width={700}
                    >
                      {selectedDocument && (
                        <Descriptions bordered column={2}>
                          <Descriptions.Item label="Document ID" span={2}>
                            <strong>{selectedDocument.document_id}</strong>
                          </Descriptions.Item>
                          <Descriptions.Item label="Title" span={2}>
                            {selectedDocument.title}
                          </Descriptions.Item>
                          <Descriptions.Item label="Employee">
                            {selectedDocument.first_name} {selectedDocument.last_name}
                          </Descriptions.Item>
                          <Descriptions.Item label="Document Type">
                            <Tag color={getDocumentTypeColor(selectedDocument.document_type)}>
                              {selectedDocument.document_type?.toUpperCase()}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="File Size">
                            {(selectedDocument.file_size / 1024).toFixed(1)} KB
                          </Descriptions.Item>
                          <Descriptions.Item label="File Type">
                            {selectedDocument.mime_type}
                          </Descriptions.Item>
                          <Descriptions.Item label="Status">
                            <Tag color={getStatusColor(selectedDocument.status)}>
                              {selectedDocument.status?.toUpperCase()}
                            </Tag>
                          </Descriptions.Item>
                          <Descriptions.Item label="Access Level">
                            {selectedDocument.access_level?.replace('_', ' ').toUpperCase()}
                          </Descriptions.Item>
                          <Descriptions.Item label="Confidential">
                            {selectedDocument.is_confidential === 1 ? 'Yes' : 'No'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Expiry Date">
                            {selectedDocument.expiry_date ? dayjs(selectedDocument.expiry_date).format('MMMM DD, YYYY') : 'No expiry'}
                          </Descriptions.Item>
                          <Descriptions.Item label="Uploaded Date" span={2}>
                            {dayjs(selectedDocument.created_at).format('MMMM DD, YYYY HH:mm')}
                          </Descriptions.Item>
                          {selectedDocument.description && (
                            <Descriptions.Item label="Description" span={2}>
                              {selectedDocument.description}
                            </Descriptions.Item>
                          )}
                        </Descriptions>
                      )}
                    </Modal>
            </div>
          ),
        },
        {
          key: 'expiry',
          label: '⚠️ Expiry Alerts',
          children: (
            <div>
              {expiryAlerts.already_expired?.length > 0 && (
                <Alert message={`${expiryAlerts.already_expired.length} documents already expired`} type="error" showIcon style={{ marginBottom: 16 }}
                  description={expiryAlerts.already_expired.map((d: any) => `${d.title} (${d.employee_name})`).join(', ')} />
              )}
              <Card title="Documents Expiring in Next 30 Days" extra={<Button onClick={fetchExpiryAlerts}>Refresh</Button>}>
                <Table dataSource={expiryAlerts.expiring_soon || []} rowKey="id" pagination={{ pageSize: 15 }}
                  columns={[
                    { title: 'Document', dataIndex: 'title' },
                    { title: 'Employee', dataIndex: 'employee_name' },
                    { title: 'Department', dataIndex: 'department' },
                    { title: 'Type', dataIndex: 'document_type', render: (v: string) => <Tag>{v}</Tag> },
                    { title: 'Expiry Date', dataIndex: 'expiry_date' },
                    { title: 'Days Left', dataIndex: 'days_until_expiry', render: (v: number) => <Tag color={v<=7?'error':v<=14?'warning':'processing'}>{v} days</Tag> },
                  ]} />
              </Card>
            </div>
          ),
        },
        {
          key: 'versions',
          label: '📋 Version History',
          children: (
            <div>
              <Alert message="Select a document from All Documents tab and click the version icon to view its history." type="info" showIcon style={{ marginBottom: 16 }} />
              {selectedDocForVersion && (
                <Card title={`Version History: ${selectedDocForVersion.title}`}>
                  <Table dataSource={docVersions} rowKey="id" pagination={false}
                    columns={[
                      { title: 'Version', dataIndex: 'version', render: (v: number) => <Tag color="blue">v{v}</Tag> },
                      { title: 'Uploaded By', dataIndex: 'uploaded_by_name' },
                      { title: 'Change Notes', dataIndex: 'change_notes' },
                      { title: 'Date', dataIndex: 'created_at', render: (v: string) => v?.split('T')[0] },
                    ]} />
                </Card>
              )}
            </div>
          ),
        },
        {
          key: 'esignature',
          label: '✍️ E-Signature',
          children: (
            <div>
              <Alert message="Draw your signature below to sign a document. Signatures are stored securely with timestamp and employee ID." type="info" showIcon style={{ marginBottom: 16 }} />
              <Card title="Sign a Document">
                <Row gutter={24}>
                  <Col span={12}>
                    <p style={{ marginBottom: 8, fontWeight: 600 }}>Select Document:</p>
                    <Select style={{ width: '100%', marginBottom: 16 }} placeholder="Select document to sign"
                      onChange={(id: any) => {
                        const doc = safeDocuments.find((d: any) => d.id === id); setSignDoc(doc || { id });
                      }}>
                      {documents.map((d: any) => (
                        <Select.Option key={d.id} value={d.id}>{d.title} ({d.document_type})</Select.Option>
                      ))}
                    </Select>
                    <p style={{ marginBottom: 8, fontWeight: 600 }}>Draw Your Signature:</p>
                    <div style={{ border: '2px solid #1890ff', borderRadius: 8, display: 'inline-block', cursor: 'crosshair' }}>
                      <canvas ref={canvasRef} width={400} height={150}
                        style={{ display: 'block', background: '#fafafa' }}
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={stopDraw}
                        onMouseLeave={stopDraw}
                      />
                    </div>
                    <div style={{ marginTop: 12 }}>
                      <Space>
                        <Button onClick={clearSignature}>Clear</Button>
                        <Button type="primary" onClick={saveSignature} disabled={!signatureData}>Sign Document</Button>
                      </Space>
                    </div>
                  </Col>
                  <Col span={12}>
                    <p style={{ fontWeight: 600, marginBottom: 8 }}>Signature Preview:</p>
                    {signatureData ? (
                      <img src={signatureData} alt="Signature" style={{ border: '1px solid #d9d9d9', borderRadius: 4, width: '100%', maxWidth: 400 }} />
                    ) : (
                      <div style={{ border: '1px dashed #d9d9d9', borderRadius: 4, padding: 40, textAlign: 'center', color: '#999' }}>Signature will appear here</div>
                    )}
                    <div style={{ marginTop: 16, padding: 12, background: '#f9f9f9', borderRadius: 4 }}>
                      <p style={{ margin: 0, fontSize: 12, color: '#666' }}>📋 Digital signature includes:</p>
                      <ul style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        <li>Timestamp: {new Date().toLocaleString()}</li>
                        <li>IP Address recorded</li>
                        <li>Employee ID verified</li>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </Card>
              <Card title="Signed Documents" style={{ marginTop: 16 }} extra={
                <Button size="small" onClick={() => {
                  fetch(`https://nexus-hr.talenthubupskilling.com/backend/hr-api/documents/documents.php?action=get_signatures&tenant_id=${apiService.getTenantId()}&document_id=all`)
                    .then(r => r.json()).then(d => { if (d.success) setSignatures(d.data || []); });
                }}>Refresh</Button>
              }>
                <Table dataSource={signatures} rowKey="id" pagination={{ pageSize: 10 }}
                  columns={[
                    { title: 'Document', dataIndex: 'document_title', render: (v: string, r: any) => <><div style={{ fontWeight: 600 }}>{v}</div><Tag style={{ fontSize: 10 }}>{r.document_type}</Tag></> },
                    { title: 'Signed By', dataIndex: 'employee_name' },
                    { title: 'Signed At', dataIndex: 'signed_at', render: (v: string) => v?.replace('T',' ').split('.')[0] },
                    { title: 'Signature', dataIndex: 'signature_data', render: (v: string) => v ? <img src={v} alt="sig" style={{ height: 40, border: '1px solid #eee', borderRadius: 4 }} /> : '—' },
                  ]} />
              </Card>
            </div>
          ),
        },
      ]} />
    </div>
  );
};

export default DocumentManagement;
